import React from 'react'
import {useTranslation} from 'react-i18next'
import {KTSVG} from 'app/utils/helpers'

export const FORM_ICONS = {
  close: '/assets/images/icons/close-line.svg',
  info: '/assets/images/icons/info-circle-line.svg',
  infoBadge: '/assets/images/icons/info-badge.svg',
  chevronDown: '/assets/images/icons/chevron-down-line.svg',
  chevronRight: '/assets/images/icons/chevron-right-line.svg',
  image: '/assets/images/icons/image-line.svg',
  clock: '/assets/images/icons/clock-line.svg',
  repeat: '/assets/images/icons/repeat-line.svg',
  lock: '/assets/images/icons/lock-line.svg',
}

export interface SelectFieldOption {
  value: string
  label: string
}

interface SelectFieldProps {
  id: string
  label: string
  placeholder: string
  required?: boolean
  value: string
  options: SelectFieldOption[]
  disabled?: boolean
  error?: string
  // false: takes one grid column instead of a whole row
  fullRow?: boolean
  onChange: (value: string) => void
}

export const SelectField: React.FC<SelectFieldProps> = ({
  id,
  label,
  placeholder,
  required,
  value,
  options,
  disabled,
  error,
  fullRow = true,
  onChange,
}) => (
  <div className={`service-form__field${fullRow ? ' service-form__field--full' : ''}`}>
    <FieldLabel label={label} required={required} htmlFor={id} />
    <div
      className={`service-form__select${fullRow ? '' : ' service-form__select--control'}${
        error ? ' service-form__control--invalid' : ''
      }`}
    >
      <select
        id={id}
        className='service-form__select-input'
        value={value}
        disabled={disabled}
        aria-invalid={Boolean(error)}
        aria-describedby={error ? `${id}-error` : undefined}
        onChange={(event) => onChange(event.target.value)}
      >
        <option value='' disabled>
          {placeholder}
        </option>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      <span className='service-form__select-chevron'>
        <KTSVG defaultColor={true} path={FORM_ICONS.chevronDown} />
      </span>
    </div>
    {error && (
      <p className='service-form__error' id={`${id}-error`} role='alert'>
        {error}
      </p>
    )}
  </div>
)

interface FieldLabelProps {
  label: string
  hint?: string
  required?: boolean
  htmlFor?: string
}

export const FieldLabel: React.FC<FieldLabelProps> = ({label, hint, required, htmlFor}) => (
  <label className='service-form__label' htmlFor={htmlFor}>
    {label}
    {hint && <span className='service-form__label-hint'>{hint}</span>}
    {required && <span className='service-form__required'>*</span>}
  </label>
)

interface TextFieldProps {
  id: string
  label: string
  labelHint?: string
  placeholder?: string
  required?: boolean
  suffix?: string
  hint?: string
  value: string
  compact?: boolean
  icon?: string
  error?: string
  disabled?: boolean
  onChange: (value: string) => void
  onBlur?: () => void
}

export const TextField: React.FC<TextFieldProps> = ({
  id,
  label,
  labelHint,
  placeholder,
  required,
  suffix,
  hint,
  value,
  compact,
  icon,
  error,
  disabled,
  onChange,
  onBlur,
}) => (
  <div className='service-form__field'>
    <FieldLabel label={label} hint={labelHint} required={required} htmlFor={id} />
    <div
      className={`service-form__control${compact ? ' service-form__control--tall' : ''}${
        error ? ' service-form__control--invalid' : ''
      }${disabled ? ' service-form__control--disabled' : ''}`}
    >
      {icon && (
        <span className='service-form__control-icon'>
          <KTSVG defaultColor={true} path={icon} />
        </span>
      )}
      <input
        id={id}
        type='text'
        className='service-form__input'
        placeholder={placeholder}
        value={value}
        disabled={disabled}
        aria-invalid={Boolean(error)}
        aria-describedby={error ? `${id}-error` : undefined}
        onChange={(event) => onChange(event.target.value)}
        onBlur={onBlur}
      />
      {suffix && <span className='service-form__suffix'>{suffix}</span>}
    </div>
    {hint && <p className='service-form__hint'>{hint}</p>}
    {error && (
      <p className='service-form__error' id={`${id}-error`} role='alert'>
        {error}
      </p>
    )}
  </div>
)

interface TextAreaFieldProps {
  id: string
  label: string
  labelHint?: string
  value: string
  onChange: (value: string) => void
}

export const TextAreaField: React.FC<TextAreaFieldProps> = ({
  id,
  label,
  labelHint,
  value,
  onChange,
}) => (
  <div className='service-form__field'>
    <FieldLabel label={label} hint={labelHint} htmlFor={id} />
    <textarea
      id={id}
      className='service-form__textarea'
      value={value}
      onChange={(event) => onChange(event.target.value)}
    />
  </div>
)

export type VatTreatment = 'excl' | 'incl'

interface VatTreatmentFieldProps {
  value: VatTreatment
  disabled?: boolean
  onChange: (value: VatTreatment) => void
}

export const VatTreatmentField: React.FC<VatTreatmentFieldProps> = ({
  value,
  disabled,
  onChange,
}) => {
  const {t} = useTranslation()
  const options: {key: VatTreatment; labelKey: string}[] = [
    {key: 'excl', labelKey: 'SERVICE_MANAGEMENT_PAGE.SERVICE_FORM.VAT_EXCL'},
    {key: 'incl', labelKey: 'SERVICE_MANAGEMENT_PAGE.SERVICE_FORM.VAT_INCL'},
  ]

  return (
    <div className='service-form__field'>
      <FieldLabel label={t('SERVICE_MANAGEMENT_PAGE.SERVICE_FORM.VAT_TREATMENT')} />
      <div className='service-form__segments' role='group'>
        {options.map((option) => (
          <button
            key={option.key}
            type='button'
            className={`service-form__segment${
              value === option.key ? ' service-form__segment--active' : ''
            }`}
            aria-pressed={value === option.key}
            disabled={disabled}
            onClick={() => onChange(option.key)}
          >
            {t(option.labelKey)}
          </button>
        ))}
      </div>
    </div>
  )
}

interface ToggleRowProps {
  label: string
  checked: boolean
  disabled?: boolean
  locked?: boolean
  children?: React.ReactNode
  onChange?: () => void
}

export const ToggleRow: React.FC<ToggleRowProps> = ({
  label,
  checked,
  disabled,
  locked,
  children,
  onChange,
}) => (
  <div className='service-form__toggle-row'>
    <button
      type='button'
      role='switch'
      aria-checked={checked}
      aria-label={label}
      aria-disabled={locked}
      disabled={disabled}
      className={`service-form__toggle${checked ? ' service-form__toggle--on' : ''}${
        locked ? ' service-form__toggle--locked' : ''
      }`}
      onClick={onChange}
    >
      <span className='service-form__toggle-knob' />
    </button>
    <span className='service-form__toggle-label'>{label}</span>
    {children}
  </div>
)

interface CollapsibleSectionProps {
  title: string
  badge?: string
  expanded: boolean
  // Locks the fields in the body only; the header still opens and closes the section.
  disabled?: boolean
  children: React.ReactNode
  onToggle: () => void
}

export const CollapsibleSection: React.FC<CollapsibleSectionProps> = ({
  title,
  badge,
  expanded,
  disabled = false,
  children,
  onToggle,
}) => (
  <section className='service-form__section'>
    <button
      type='button'
      className='service-form__section-header'
      aria-expanded={expanded}
      onClick={onToggle}
    >
      <span className='service-form__section-chevron'>
        <KTSVG
          defaultColor={true}
          path={expanded ? FORM_ICONS.chevronDown : FORM_ICONS.chevronRight}
        />
      </span>
      <span className='service-form__section-title'>{title}</span>
      {Boolean(badge) && <span className='service-form__section-badge'>{badge}</span>}
    </button>
    {expanded && (
      <fieldset className='service-form__fieldset' disabled={disabled}>
        <div className='service-form__section-body'>{children}</div>
      </fieldset>
    )}
  </section>
)
